import { type TouchEvent, useCallback, useRef, useState } from 'react';

/**
 * Swipe doleva odhalí akce řádku (kap. 28, 37). Práh 60 px; svislý pohyb (scroll) swipe ruší.
 * Vrací posun obsahu a handlery pro dotyk; na desktopu se akce ukazují při najetí přes CSS.
 */
export const SWIPE_WIDTH = 150;
const THRESHOLD = 60;

export function useSwipe(onOpenChange?: (open: boolean) => void) {
  const [offset, setOffset] = useState(0);
  const start = useRef<{ x: number; y: number; open: boolean } | null>(null);
  const cancelled = useRef(false);

  const onTouchStart = useCallback(
    (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      start.current = { x: t.clientX, y: t.clientY, open: offset < 0 };
      cancelled.current = false;
    },
    [offset],
  );

  const onTouchMove = useCallback((e: TouchEvent) => {
    const t = e.touches[0];
    const s = start.current;
    if (!t || !s || cancelled.current) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) {
      cancelled.current = true;
      setOffset(s.open ? -SWIPE_WIDTH : 0);
      return;
    }
    const base = s.open ? -SWIPE_WIDTH : 0;
    setOffset(Math.max(-SWIPE_WIDTH, Math.min(0, base + dx)));
  }, []);

  const onTouchEnd = useCallback(() => {
    const s = start.current;
    start.current = null;
    if (!s || cancelled.current) return;
    setOffset((cur) => {
      const open = s.open ? cur > -SWIPE_WIDTH + THRESHOLD : cur < -THRESHOLD;
      onOpenChange?.(open);
      return open ? -SWIPE_WIDTH : 0;
    });
  }, [onOpenChange]);

  const close = useCallback(() => setOffset(0), []);

  return { offset, open: offset < 0, handlers: { onTouchStart, onTouchMove, onTouchEnd }, close };
}
